import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { compose } from 'redux';
import categoryConnector from '@frontastic/catwalk/src/js/app/connector/category';
import tastify from '@frontastic/catwalk/src/js/helper/tastify';
import { sdk } from '../../../../sdk';
import Australia from '../../components/atoms/icons/Australia';
import NewZealand from '../../components/atoms/icons/NewZealand';
import { GlobalSchema } from '../../components/molecules/globalSchema';
import { HomePageSchema } from '../../components/molecules/homePageSchema';
import Header from '../../components/organisms/header';
import { login, register } from '../../redux/loader/cart';

const localesOption = [
  {
    label: 'NZD',
    value: 'en_AU@NZD',
    special: <NewZealand width="29px" height="17px" />,
  },
  {
    label: 'AUD',
    value: 'en_AU@AUD',
    special: <Australia width="29px" height="17px" />,
  },
];

const mergedHeaderData = (innerData, outerData) => {
  if (!outerData) {
    return innerData;
  }

  return {
    ...innerData,
    promoText: outerData?.promoText || innerData?.promoText,
    promoTextColor: outerData?.promoTextColor || innerData?.promoTextColor,
    promoReference: outerData?.promoReference || innerData?.promoReference,
    promoBackground: outerData?.promoBackground || innerData?.promoBackground,
    contentfulNavigationTiles: outerData?.navigationPromoTilesCollection?.items ?? [],
  };
};

const HeaderTastic = ({ data, context, node, isServerSideRendering }) => {
  const isSSR = typeof isServerSideRendering === 'boolean' && isServerSideRendering;

  const [headerOuterData, setHeaderOuterData] = useState(null);

  const triggerRoute = useSelector((state) => state['demo-common'].triggerRoute);
  const isHomePageLoaded = typeof window !== 'undefined' && window.location.pathname === '/';

  const { headerData, configuration, loggedIn, customerGroupId, locale: siteLocale } = useMemo(() => {
    return {
      headerData: mergedHeaderData(data, headerOuterData),
      configuration: node?.configuration,
      loggedIn: context.session.loggedIn,
      locale: context?.locale,
      customerGroupId: context.session?.account?.projectSpecificData?.customerGroupId ?? null,
    };
  }, [data, context, node, headerOuterData]);

  const locale = siteLocale === 'en_AU@AUD' ? 'AU' : 'NZ';
  if (!data[`show${locale}`]) return null;

  const {
    crossBrandTraffic,
    topCategories,
    promo,
    promoText,
    promoTextColor,
    promoReference,
    promoBackground,
    reference,
    shippingCoastMessage,
    accountDropdownContentBlock,
    accountDropdownPromo,
    recentlyViewedPlacement,
    recentlyViewedTitle,
    recommenderPlacement,
    recommenderTitle,
    contentfulNavigationTiles,
    crossBrandTrafficColors,
  } = headerData || {};

  const { pageType = 'fullwidth', isTransparent = false, opacityLevel = 1, showCanonicalLink = true } = configuration;

  const [isTransparentHeader, setIsTransparentHeader] = useState(false);

  const nostoBlocksData = {
    recentlyViewedPlacement,
    recentlyViewedTitle,
    recommenderPlacement,
    recommenderTitle,
  };

  useEffect(() => {
    if (!isSSR) {
      document.body.classList.add(pageType || 'fullwidth');

      return () => {
        document.body.classList.remove(pageType || 'fullwidth');
      };
    }
  }, [pageType, isSSR, isTransparent]);

  useEffect(() => {
    setIsTransparentHeader(isTransparent);
    return () => {
      setIsTransparentHeader(false);
    };
  }, [triggerRoute]);

  useEffect(() => {
    const fetchHeaderDetails = async () => {
      try {
        const response = await sdk.callAction({
          actionName: '/contentful/getBrandHeaderDetails',
        });
        if (!response.isError) {
          setHeaderOuterData(response?.data);
        }
      } catch (error) {
        console.error('getHeaderDetails error:', error);
      }
    };

    fetchHeaderDetails();
  }, []);

  if (!data) return null;

  return (
    <>
      <GlobalSchema showCanonicalLink={showCanonicalLink} />
      {isHomePageLoaded && <HomePageSchema />}
      <Header
        crossBrandTraffic={crossBrandTraffic}
        promo={promo}
        accountDropdownContentBlock={accountDropdownContentBlock}
        accountDropdownPromo={accountDropdownPromo}
        loggedIn={loggedIn}
        customerGroupId={customerGroupId}
        promoText={promoText}
        promoTextColor={promoTextColor || null}
        promoLink={promoReference}
        promoBackground={promoBackground || null}
        showCanonicalLink={showCanonicalLink}
        shippingCoastMessage={shippingCoastMessage}
        categories={topCategories}
        locales={localesOption}
        storeLocatorLink={reference}
        isTransparent={isTransparentHeader}
        opacityLevel={opacityLevel}
        handleRegister={register}
        handleLogin={login}
        isServerSideRendering={isSSR}
        nostoBlocks={nostoBlocksData}
        contentfulNavigationTiles={contentfulNavigationTiles}
        crossBrandTrafficColors={crossBrandTrafficColors}
        siteLocale={siteLocale}
      />
    </>
  );
};

HeaderTastic.propTypes = {
  data: PropTypes.object.isRequired,
};

export default tastify({
  translate: true,
  connect: {
    node: true,
    tastic: true,
    route: true,
    context: true,
    isServerSideRendering: true,
  },
})(
  compose(
    connect(categoryConnector),
    connect((globalState) => {
      const streamParameters = globalState.app.route.parameters.s || {};

      return {
        route: globalState.app.route,
        streamParameters,
      };
    }),
  )(HeaderTastic),
);
